import { LitElement, html, nothing } from 'lit';
import { unsafeHTML } from 'lit/directives/unsafe-html.js';
import { customElement, property, state } from 'lit/decorators.js';
import { marked } from 'marked';
import DOMPurify from 'dompurify';
import type { PendingStrategy, AgentStrategy } from '../../services/company-api.js';
import { approveStrategy, reviseStrategy, submitStrategyClarification } from '../../services/company-api.js';

@customElement('cp-strategy-approval-card')
export class CpStrategyApprovalCard extends LitElement {
  createRenderRoot() { return this; }

  @property({ type: Object }) strategy!: PendingStrategy;

  @state() private _panelOpen = false;
  @state() private _showReply = false;
  @state() private _replyText = '';
  @state() private _submitting = false;
  @state() private _error = '';

  private _renderMarkdown(content: string): ReturnType<typeof html> {
    const rawHtml = marked.parse(content, { async: false }) as string;
    const sanitized = DOMPurify.sanitize(rawHtml);
    return html`${unsafeHTML(sanitized)}`;
  }

  private async _handleApprove(): Promise<void> {
    this._submitting = true;
    this._error = '';
    try {
      const updated = await approveStrategy(this.strategy.id);
      this._panelOpen = false;
      this._dispatchUpdate(updated);
    } catch (e) {
      this._error = e instanceof Error ? e.message : 'Fehler beim Genehmigen';
    } finally {
      this._submitting = false;
    }
  }

  private async _handleRevise(): Promise<void> {
    if (!this._replyText.trim()) return;
    this._submitting = true;
    this._error = '';
    try {
      const updated = await reviseStrategy(this.strategy.id, this._replyText.trim());
      this._panelOpen = false;
      this._dispatchUpdate(updated);
      this._showReply = false;
      this._replyText = '';
    } catch (e) {
      this._error = e instanceof Error ? e.message : 'Fehler beim Ueberarbeiten';
    } finally {
      this._submitting = false;
    }
  }

  private async _handleClarify(): Promise<void> {
    if (!this._replyText.trim()) return;
    this._submitting = true;
    this._error = '';
    try {
      const updated = await submitStrategyClarification(this.strategy.id, this._replyText.trim());
      this._panelOpen = false;
      this._dispatchUpdate(updated);
      this._showReply = false;
      this._replyText = '';
    } catch (e) {
      this._error = e instanceof Error ? e.message : 'Fehler beim Beantworten';
    } finally {
      this._submitting = false;
    }
  }

  private _dispatchUpdate(updated: AgentStrategy): void {
    this.dispatchEvent(new CustomEvent('strategy-updated', {
      detail: { strategy: updated },
      bubbles: true,
      composed: true,
    }));
  }

  private _openPanel(): void {
    this._panelOpen = true;
  }

  private _closePanel(): void {
    this._panelOpen = false;
    this._showReply = false;
    this._replyText = '';
    this._error = '';
  }

  private _onOverlayClick(e: MouseEvent): void {
    if ((e.target as HTMLElement).classList.contains('cp-sa-panel__overlay')) {
      this._closePanel();
    }
  }

  private _onKeyDown = (e: KeyboardEvent): void => {
    if (e.key === 'Escape') this._closePanel();
  };

  override connectedCallback(): void {
    super.connectedCallback();
    document.addEventListener('keydown', this._onKeyDown);
  }

  override disconnectedCallback(): void {
    super.disconnectedCallback();
    document.removeEventListener('keydown', this._onKeyDown);
  }

  private _stateLabel(state: string): string {
    return state === 'strategy_review' ? 'Strategie-Review' : 'Rueckfragen offen';
  }

  // --- Card (compact inline view) ---

  private _renderCard() {
    const { strategy } = this;
    const isReview = strategy.state === 'strategy_review';
    const isClarifying = strategy.state === 'clarifying';

    return html`
      <div class="cp-sa" @click=${() => this._openPanel()}>
        <div class="cp-sa__header">
          <div class="cp-sa__title-area">
            <p class="cp-sa__title">${strategy.goal_title}</p>
            <p class="cp-sa__meta">${strategy.company_name} &middot; ${strategy.agent_name}</p>
          </div>
          <span class="cp-sa__state-badge cp-sa__state-badge--${strategy.state}">
            ${this._stateLabel(strategy.state)}
          </span>
        </div>
        <div class="cp-sa__quick-actions" @click=${(e: Event) => e.stopPropagation()}>
          ${isReview ? html`
            <button
              class="cp-sa__btn cp-sa__btn--approve"
              ?disabled=${this._submitting}
              @click=${() => void this._handleApprove()}
            >Genehmigen</button>
            <button
              class="cp-sa__btn cp-sa__btn--ghost"
              @click=${() => this._openPanel()}
            >Details</button>
          ` : nothing}
          ${isClarifying ? html`
            <button
              class="cp-sa__btn cp-sa__btn--primary"
              @click=${() => { this._panelOpen = true; this._showReply = true; }}
            >Beantworten</button>
          ` : nothing}
        </div>
      </div>
    `;
  }

  // --- Panel (fullscreen detail view) ---

  private _renderPanel() {
    const { strategy } = this;
    const isClarifying = strategy.state === 'clarifying';
    const isReview = strategy.state === 'strategy_review';

    return html`
      <div class="cp-sa-panel__overlay" @click=${this._onOverlayClick}>
        <div class="cp-sa-panel">
          <div class="cp-sa-panel__header">
            <div>
              <h2 class="cp-sa-panel__title">${strategy.goal_title}</h2>
              <p class="cp-sa-panel__meta">${strategy.company_name} &middot; ${strategy.agent_name}</p>
            </div>
            <div class="cp-sa-panel__header-right">
              <span class="cp-sa__state-badge cp-sa__state-badge--${strategy.state}">
                ${this._stateLabel(strategy.state)}
              </span>
              <button class="cp-sa-panel__close" @click=${() => this._closePanel()}>&times;</button>
            </div>
          </div>

          <div class="cp-sa-panel__body">
            ${strategy.goal_description ? html`
              <div class="cp-sa-panel__section">
                <h3 class="cp-sa-panel__section-label">Ziel</h3>
                <div class="cp-sa__content-md">${this._renderMarkdown(strategy.goal_description)}</div>
              </div>
            ` : nothing}

            ${isReview && strategy.strategy_content ? html`
              <div class="cp-sa-panel__section">
                <h3 class="cp-sa-panel__section-label">Strategieplan</h3>
                <div class="cp-sa__content-md">${this._renderMarkdown(strategy.strategy_content)}</div>
              </div>
            ` : nothing}

            ${isClarifying && strategy.clarifying_questions ? html`
              <div class="cp-sa-panel__section">
                <h3 class="cp-sa-panel__section-label">Rueckfragen des Agenten</h3>
                <div class="cp-sa__content-md">${this._renderMarkdown(strategy.clarifying_questions)}</div>
              </div>
            ` : nothing}
          </div>

          <div class="cp-sa-panel__footer">
            ${this._error ? html`<p class="cp-sa__error">${this._error}</p>` : nothing}

            ${!this._showReply ? html`
              <div class="cp-sa__actions">
                ${isReview ? html`
                  <button class="cp-sa__btn cp-sa__btn--approve" ?disabled=${this._submitting}
                    @click=${() => this._handleApprove()}>Genehmigen</button>
                  <button class="cp-sa__btn cp-sa__btn--ghost"
                    @click=${() => { this._showReply = true; }}>Ueberarbeiten lassen</button>
                ` : nothing}
                ${isClarifying ? html`
                  <button class="cp-sa__btn cp-sa__btn--primary"
                    @click=${() => { this._showReply = true; }}>Fragen beantworten</button>
                ` : nothing}
              </div>
            ` : html`
              <div class="cp-sa__reply-form">
                <textarea
                  rows="4"
                  placeholder=${isClarifying ? 'Antworten auf die Rueckfragen...' : 'Feedback zur Ueberarbeitung...'}
                  .value=${this._replyText}
                  @input=${(e: Event) => { this._replyText = (e.target as HTMLTextAreaElement).value; }}
                  class="cp-sa__textarea"
                ></textarea>
                <div class="cp-sa__actions">
                  ${isClarifying ? html`
                    <button class="cp-sa__btn cp-sa__btn--primary"
                      ?disabled=${this._submitting || !this._replyText.trim()}
                      @click=${() => this._handleClarify()}>Senden</button>
                  ` : html`
                    <button class="cp-sa__btn cp-sa__btn--approve"
                      ?disabled=${this._submitting || !this._replyText.trim()}
                      @click=${() => this._handleRevise()}>Zur Ueberarbeitung</button>
                  `}
                  <button class="cp-sa__btn cp-sa__btn--ghost"
                    @click=${() => { this._showReply = false; this._replyText = ''; }}>Abbrechen</button>
                </div>
              </div>
            `}
          </div>
        </div>
      </div>
    `;
  }

  override render() {
    return html`
      ${this._renderCard()}
      ${this._panelOpen ? this._renderPanel() : nothing}
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'cp-strategy-approval-card': CpStrategyApprovalCard;
  }
}
